import { token } from '@/infrastructure/container';
import { EventBus, type EventSubscription, type SubscribeOptions } from '@/infrastructure/EventBus';
import { type ILogger } from './Logger';
import { type IMetrics } from './Metrics';

// Typed cross-store pub/sub. For cross-cutting signals that would otherwise force a store-to-store
// import or a service-to-store one (an upward edge, INVARIANTS rule 1).
//
// One dispatch mode, `publish`, fire and forget. See src/infrastructure/EventBus.ts for why this
// app ships one where the template ships three.

export type AppEvents = {
  TOAST: { kind: 'info' | 'success' | 'warn' | 'error'; message: string };
  // navigator.onLine flipped. grading-day is online-only: every screen gates on this, and a
  // dropped connection has to be visible rather than inferred from a failed write.
  CONNECTIVITY_CHANGED: { online: boolean };
  // The address bar moved. Role routing reads it: a scanned share link lands the device on a URL
  // and the app resolves which of the six roles this device is.
  URL_CHANGED: { path: string };
};

// Whether a runtime publish with zero subscribers is a bug. 'required' events must have a live
// handler at boot (assertBootSubscribers) and earn a bus.zero_handlers trace if ever published
// unheard; 'quiet-ok' events legitimately tolerate silence. The compiler owns the list: a new
// event fails typecheck until it is classified here.
//
// Everything is 'required' today. That is a fact about the current event map, not a policy: the
// first genuine broadcast this app grows gets classified 'quiet-ok' and the machinery already
// handles it.
export const BUS_SUBSCRIBER_POLICY = {
  TOAST: 'required',
  CONNECTIVITY_CHANGED: 'required',
  URL_CHANGED: 'required',
} as const satisfies Record<keyof AppEvents, 'required' | 'quiet-ok'>;

export interface IAppBus {
  publish<K extends keyof AppEvents>(type: K, data: AppEvents[K]): void;
  subscribe<K extends keyof AppEvents>(
    type: K,
    handler: (data: AppEvents[K]) => void,
    options?: SubscribeOptions,
  ): EventSubscription;
  once<K extends keyof AppEvents>(
    type: K,
    handler: (data: AppEvents[K]) => void,
    options?: SubscribeOptions,
  ): EventSubscription;
  toast(kind: AppEvents['TOAST']['kind'], message: string): void;
  connectivityChanged(online: boolean): void;
  urlChanged(path: string): void;
}

export class AppBus implements IAppBus {
  private readonly bus: EventBus<AppEvents>;

  constructor(
    logger: ILogger,
    private readonly metrics: IMetrics,
  ) {
    this.bus = new EventBus<AppEvents>(logger);
  }

  publish<K extends keyof AppEvents>(type: K, data: AppEvents[K]): void {
    this.metrics.counter('app_bus_published_total', { event: type });
    this.bus.publish(type, data);
  }

  subscribe<K extends keyof AppEvents>(
    type: K,
    handler: (data: AppEvents[K]) => void,
    options?: SubscribeOptions,
  ): EventSubscription {
    return this.bus.subscribe(type, handler, options);
  }

  once<K extends keyof AppEvents>(
    type: K,
    handler: (data: AppEvents[K]) => void,
    options?: SubscribeOptions,
  ): EventSubscription {
    return this.bus.once(type, handler, options);
  }

  toast(kind: AppEvents['TOAST']['kind'], message: string): void {
    this.publish('TOAST', { kind, message });
  }

  connectivityChanged(online: boolean): void {
    this.publish('CONNECTIVITY_CHANGED', { online });
  }

  urlChanged(path: string): void {
    this.publish('URL_CHANGED', { path });
  }

  // Introspection for boot wiring and tests only. Kept off IAppBus so injected stores cannot
  // branch on how many others are listening.
  handlerCount<K extends keyof AppEvents>(type: K): number {
    return this.bus.handlerCount(type);
  }
}

export const TOKEN_IAppBus = token<IAppBus>('IAppBus');
